import { AlertCircle, Camera, Image as ImageIcon, Trash2, WifiOff } from "lucide-react";
import { type ChangeEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import {
  type EstadoLoto,
  type FotoLoto,
  type LotoDetail,
  textoPuntos,
  useBorrarFotoLoto,
  useFotosLoto,
  useSubirFotoLoto,
} from "@/lib/lotos";
import { useEstadoConexion } from "@/lib/online-status";
import { ESTADO_LOTO } from "./estado";
import { type GaleriaImagen, Lightbox } from "./Lightbox";

// MAX_FOTOS_LOTO espeja el tope del backend (MaxFotosPorLoto). Al alcanzarlo se deshabilita el alta.
const MAX_FOTOS_LOTO = 10;

// formatFecha da un instante ISO en formato local es-ES (día y hora). "" si no es válido.
function formatFecha(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// faseLabel traduce la fase estampada por el servidor (ABIERTO/CERRADO, #241) a su etiqueta.
function faseLabel(fase: string): string {
  return ESTADO_LOTO[fase as EstadoLoto]?.label ?? fase;
}

// mensajeSubida traduce el fallo permanente de dominio al subir una foto de LOTO.
function mensajeSubida(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "MAX_FOTOS":
        return `El LOTO ya tiene el máximo de ${MAX_FOTOS_LOTO} fotos.`;
      case "TIPO_INVALIDO":
        return "La foto debe ser una imagen JPEG, PNG o WebP.";
      case "ARCHIVO_DEMASIADO_GRANDE":
        return "La foto supera el tamaño máximo permitido (8 MB).";
      default:
        return err.message || "No se pudo subir la foto. Inténtalo de nuevo.";
    }
  }
  return "No se pudo subir la foto. Inténtalo de nuevo.";
}

type Filtro = "todas" | "loto" | "candado";

/**
 * RegistroFotografico es la sección de fotos de la ficha del LOTO (#212): el registro fotográfico
 * propio del LOTO (con su fase y comentario) MÁS las fotos de verificación de cada candado, en una
 * galería agregada, filtrable por origen y con un lightbox a pantalla completa. Quien puede registrar
 * añade fotos al LOTO (hasta 10, con captura de cámara/galería); ADMIN/ADMIN_OBRA puede borrarlas.
 * Las imágenes se sirven autenticadas por cookie mismo-origen, así que `<img src>` funciona directo.
 */
export function RegistroFotografico({
  loto,
  obraId,
  puedeRegistrar,
  puedeBorrar,
}: {
  loto: LotoDetail;
  obraId: number;
  puedeRegistrar: boolean;
  puedeBorrar: boolean;
}) {
  const { data: fotosLoto } = useFotosLoto(obraId, loto.id);
  const borrar = useBorrarFotoLoto(obraId);

  const [abrirAlta, setAbrirAlta] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [aBorrar, setABorrar] = useState<FotoLoto | null>(null);

  // Nº de fotos del LOTO: la lista si ya cargó, si no el contador barato de la ficha (num_fotos).
  const numFotosLoto = fotosLoto?.length ?? loto.num_fotos;

  // Fotos del LOTO como imágenes de galería (origen "loto"): binario servido por id de foto.
  const imgsLoto = useMemo<GaleriaImagen[]>(
    () =>
      (fotosLoto ?? []).map((f) => {
        const partes = [
          formatFecha(f.created_at),
          f.subido_por_nombre || null,
          f.comentario || null,
        ].filter(Boolean);
        return {
          key: `loto-${f.id}`,
          src: `/api/v1/obras/${obraId}/lotos/${loto.id}/fotos/${f.id}`,
          alt: `Foto del LOTO — ${faseLabel(f.fase)}`,
          origen: "loto" as const,
          titulo: `LOTO · ${faseLabel(f.fase)}`,
          pie: partes.join(" · ") || undefined,
        };
      }),
    [fotosLoto, obraId, loto.id],
  );

  // Fotos de verificación de cada candado como imágenes de galería (origen "candado"): ordinal
  // 1-based por bloqueo, servidas por su endpoint bajo la obra.
  const imgsCandado = useMemo<GaleriaImagen[]>(
    () =>
      loto.bloqueos.flatMap((b) =>
        Array.from({ length: b.num_fotos }, (_, i) => i + 1).map((n) => ({
          key: `candado-${b.id}-${n}`,
          src: `/api/v1/obras/${obraId}/bloqueos/${b.id}/fotos/${n}`,
          alt: `Foto de verificación ${n} del candado ${b.codigo_candado}`,
          origen: "candado" as const,
          titulo: `Candado ${b.codigo_candado}`,
          pie: textoPuntos(b),
        })),
      ),
    [loto.bloqueos, obraId],
  );

  const hayLoto = imgsLoto.length > 0;
  const hayCandado = imgsCandado.length > 0;
  // La galería mezcla ambos orígenes: primero las del LOTO (registro propio), luego las de candados.
  const todas = useMemo(() => [...imgsLoto, ...imgsCandado], [imgsLoto, imgsCandado]);
  const visibles = useMemo(() => {
    if (filtro === "loto") {
      return imgsLoto;
    }
    if (filtro === "candado") {
      return imgsCandado;
    }
    return todas;
  }, [filtro, imgsLoto, imgsCandado, todas]);

  // El filtro solo tiene sentido si conviven los dos orígenes.
  const mostrarFiltro = hayLoto && hayCandado;

  const alcanzoTope = numFotosLoto >= MAX_FOTOS_LOTO;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-bold text-slate-900 text-xl tracking-tight">
          <ImageIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
          Registro fotográfico
        </h2>
        {puedeRegistrar ? (
          <div className="flex items-center gap-3">
            <span className="text-slate-500 text-sm tabular-nums">
              {numFotosLoto}/{MAX_FOTOS_LOTO} fotos
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAbrirAlta(true)}
              disabled={alcanzoTope}
              title={
                alcanzoTope ? `El LOTO ya tiene el máximo de ${MAX_FOTOS_LOTO} fotos.` : undefined
              }
            >
              <Camera className="h-5 w-5" aria-hidden="true" />
              Añadir foto
            </Button>
          </div>
        ) : null}
      </div>

      {mostrarFiltro ? (
        <fieldset className="flex flex-wrap gap-2 border-0 p-0">
          <legend className="sr-only">Filtrar fotos por origen</legend>
          <FiltroChip activo={filtro === "todas"} onClick={() => setFiltro("todas")}>
            Todas ({todas.length})
          </FiltroChip>
          <FiltroChip activo={filtro === "loto"} onClick={() => setFiltro("loto")}>
            LOTO ({imgsLoto.length})
          </FiltroChip>
          <FiltroChip activo={filtro === "candado"} onClick={() => setFiltro("candado")}>
            Candados ({imgsCandado.length})
          </FiltroChip>
        </fieldset>
      ) : null}

      {visibles.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-10 text-center text-slate-500">
          Aún no hay fotos en este LOTO.
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {visibles.map((img, indexEnVisibles) => {
            // La foto es del LOTO y hay permiso para borrar: se localiza su metadato para la acción.
            const fotoLoto =
              img.origen === "loto"
                ? (fotosLoto ?? []).find((f) => `loto-${f.id}` === img.key)
                : undefined;
            return (
              <li key={img.key} className="relative">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(indexEnVisibles)}
                  className="block aspect-square w-full overflow-hidden rounded-xl border border-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="h-full w-full object-cover transition-transform hover:scale-105"
                  />
                </button>
                <span className="absolute top-1.5 left-1.5 rounded-full bg-slate-900/70 px-2 py-0.5 font-semibold text-[11px] text-white">
                  {img.origen === "loto" ? "LOTO" : "Candado"}
                </span>
                {puedeBorrar && fotoLoto ? (
                  <button
                    type="button"
                    onClick={() => setABorrar(fotoLoto)}
                    aria-label="Borrar foto del LOTO"
                    className="absolute right-1.5 bottom-1.5 flex h-10 w-10 items-center justify-center rounded-full bg-slate-900/70 text-white hover:bg-red-600"
                  >
                    <Trash2 className="h-5 w-5" aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Lightbox
        imagenes={visibles}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />

      {abrirAlta ? (
        <AgregarFotoLotoModal
          loto={loto}
          obraId={obraId}
          orden={numFotosLoto + 1}
          onClose={() => setAbrirAlta(false)}
        />
      ) : null}

      <ConfirmarBorradoModal
        foto={aBorrar}
        borrando={borrar.isPending}
        error={borrar.isError}
        onConfirmar={() => {
          if (aBorrar == null) {
            return;
          }
          borrar.mutate(
            { idLoto: loto.id, idFoto: aBorrar.id },
            { onSuccess: () => setABorrar(null) },
          );
        }}
        onClose={() => setABorrar(null)}
      />
    </section>
  );
}

// FiltroChip es un botón-píldora de filtro (target táctil amplio para guantes), activo destacado.
function FiltroChip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={
        activo
          ? "min-h-10 rounded-full border border-primary-500 bg-primary-50 px-4 py-1.5 font-semibold text-primary-700 text-sm"
          : "min-h-10 rounded-full border border-slate-300 bg-white px-4 py-1.5 font-medium text-slate-600 text-sm hover:bg-slate-50"
      }
    >
      {children}
    </button>
  );
}

/**
 * AgregarFotoLotoModal captura una foto del registro fotográfico del LOTO desde la cámara/galería
 * del móvil, con previsualización y un comentario opcional. La FASE no se elige (#241): la sella
 * el servidor con el estado del LOTO. Sube con el patrón online/offline de useSubirFotoLoto: sin
 * red (o si falla) la foto se encola y el runner la sube al reconectar.
 */
function AgregarFotoLotoModal({
  loto,
  obraId,
  orden,
  onClose,
}: {
  loto: LotoDetail;
  obraId: number;
  orden: number;
  onClose: () => void;
}) {
  const subir = useSubirFotoLoto(obraId);
  const estadoConexion = useEstadoConexion();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [comentario, setComentario] = useState("");
  const [error, setError] = useState<string | null>(null);

  // GATE binario y fail-closed (#237): todo lo que no sea red CONFIRMADA cuenta como sin red —la
  // ventana «sin confirmar» del arranque incluida—, que es exactamente el `useOnlineStatus()` de
  // antes. Decide el verbo del botón porque decide el destino de la foto: `getOnline()` la sube o
  // la encola con el mismo criterio.
  const sinConexion = estadoConexion !== "online";
  // El AVISO, en cambio, solo habla con la falta de red CONFIRMADA (#339 D1): este modal se abre a
  // propósito y con la acción en marcha, así que durante la ventana se calla en vez de enseñar y
  // retirar un aviso en menos de un segundo (precedente: `lib/filtros.ts::avisoSinConexion`, #247).
  const sinConexionConfirmada = estadoConexion === "offline";

  // Previsualización de la foto elegida; se revoca al cambiar para no filtrar memoria.
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  function onElegir(e: ChangeEvent<HTMLInputElement>) {
    const elegido = e.target.files?.[0] ?? null;
    if (elegido) {
      setFile(elegido);
      setError(null);
    }
    // Permite volver a elegir el mismo archivo (el navegador no dispara change si no cambia).
    e.target.value = "";
  }

  async function confirmar() {
    if (file == null) {
      return;
    }
    setError(null);
    try {
      await subir.mutateAsync({
        idLoto: loto.id,
        file,
        comentario: comentario.trim() || null,
        orden,
      });
      onClose();
    } catch (err) {
      setError(mensajeSubida(err));
    }
  }

  return (
    <Modal open onClose={onClose} title="Añadir foto al LOTO" className="sm:max-w-lg">
      <div className="flex flex-col gap-4">
        {sinConexionConfirmada ? (
          <Alert variant="info">
            <WifiOff className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Sin conexión: la foto se guardará y se subirá al reconectar.</span>
          </Alert>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onElegir}
        />

        {preview ? (
          <img
            src={preview}
            alt="Previsualización de la foto"
            className="h-56 w-full rounded-xl border border-slate-200 object-cover"
          />
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex h-56 w-full flex-col items-center justify-center gap-2 rounded-xl border border-slate-300 border-dashed bg-slate-50 text-slate-500 hover:bg-slate-100"
          >
            <Camera className="h-10 w-10" aria-hidden="true" />
            <span className="font-medium">Toca para hacer una foto</span>
          </button>
        )}
        {preview ? (
          <Button variant="outline" size="lg" onClick={() => fileInputRef.current?.click()}>
            <Camera className="h-5 w-5" aria-hidden="true" />
            Cambiar foto
          </Button>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="comentario-foto">Comentario (opcional)</Label>
          <Textarea
            id="comentario-foto"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Describe qué muestra la foto…"
          />
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </Alert>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={subir.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} disabled={file == null || subir.isPending}>
            {sinConexion ? "Guardar foto" : "Subir foto"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ConfirmarBorradoModal pide confirmación antes de borrar una foto del registro fotográfico del
// LOTO (acción de ADMIN/ADMIN_OBRA). No renderiza nada si no hay foto seleccionada.
function ConfirmarBorradoModal({
  foto,
  borrando,
  error,
  onConfirmar,
  onClose,
}: {
  foto: FotoLoto | null;
  borrando: boolean;
  error: boolean;
  onConfirmar: () => void;
  onClose: () => void;
}) {
  if (foto == null) {
    return null;
  }
  return (
    <Modal open onClose={onClose} title="Borrar foto">
      <div className="flex flex-col gap-4">
        <p className="text-slate-600">
          ¿Seguro que quieres borrar esta foto del registro fotográfico? Esta acción no se puede
          deshacer.
        </p>
        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudo borrar la foto. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={borrando}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirmar} disabled={borrando}>
            <Trash2 className="h-5 w-5" aria-hidden="true" />
            Borrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
